

import { DailyMessage } from "../components/child/DailyMessage";

const STARS_BG = {
  backgroundImage: [
    "radial-gradient(2px 2px at 20px 30px, white, transparent)",
    "radial-gradient(2px 2px at 40px 70px, rgba(255,255,255,0.8), transparent)",
    "radial-gradient(1px 1px at 90px 40px, white, transparent)",
    "radial-gradient(2px 2px at 130px 80px, rgba(255,255,255,0.6), transparent)",
    "radial-gradient(1px 1px at 160px 120px, white, transparent)",
    "radial-gradient(2px 2px at 200px 50px, rgba(255,255,255,0.7), transparent)",
    "radial-gradient(1px 1px at 250px 160px, white, transparent)",
    "radial-gradient(2px 2px at 280px 90px, rgba(255,255,255,0.5), transparent)",
    "radial-gradient(1px 1px at 320px 200px, white, transparent)",
    "radial-gradient(2px 2px at 350px 20px, rgba(255,255,255,0.8), transparent)",
  ].join(", "),
  backgroundSize: "400px 250px",
};

const NEBULA_BG = {
  background: [
    "radial-gradient(ellipse at 30% 20%, rgba(147, 51, 234, 0.3) 0%, transparent 50%)",
    "radial-gradient(ellipse at 70% 80%, rgba(59, 130, 246, 0.2) 0%, transparent 50%)",
    "radial-gradient(ellipse at 50% 50%, rgba(236, 72, 153, 0.15) 0%, transparent 60%)",
  ].join(", "),
};

const GLASS = {
  background: "rgba(255, 255, 255, 0.05)",
  backdropFilter: "blur(10px)",
  border: "1px solid rgba(255, 255, 255, 0.1)",
};

const PLANETS = [
  { key: "spelling",  label: "SPELLING",  bg: "linear-gradient(135deg, #60a5fa, #3b82f6, #4f46e5)", shadow: "0 0 30px rgba(99,102,241,0.4)", size: 96 },
  { key: "meaning",   label: "MEANINGS",  bg: "linear-gradient(135deg, #c084fc, #a855f7, #ec4899)", shadow: "0 0 30px rgba(168,85,247,0.5)", size: 80 },
  { key: "synonym",   label: "SYNONYMS",  bg: "linear-gradient(135deg, #34d399, #14b8a6, #06b6d4)", shadow: "0 0 30px rgba(20,184,166,0.4)", size: 72 },
  { key: "antonym",   label: "ANTONYMS",  bg: "linear-gradient(135deg, #fb7185, #ef4444, #f97316)", shadow: "0 0 30px rgba(239,68,68,0.5)", size: 64 },
];

const PLANET_POSITIONS = [
  { top: "4%",  left: "4%" },
  { top: "8%",  right: "4%" },
  { bottom: "20%", left: "8%" },
  { bottom: "24%", right: "6%" },
];

export function SpaceHomeLayout({
  user, data,
  onStartTest, onStartMeaningTest, onStartSynonymTest, onStartAntonymTest,
  settings,
}) {
  const mySessions = data.sessions.filter(s => s.userId === user.id);
  const today = new Date().toDateString();
  const doneSpelling = mySessions.some(s => new Date(s.date).toDateString() === today && s.type !== "meaning" && s.type !== "synonym" && s.type !== "antonym");
  const doneMeaning  = mySessions.some(s => new Date(s.date).toDateString() === today && s.type === "meaning");
  const doneSynonym  = mySessions.some(s => new Date(s.date).toDateString() === today && s.type === "synonym");
  const doneAntonym  = mySessions.some(s => new Date(s.date).toDateString() === today && s.type === "antonym");
  const doneMap = { spelling: doneSpelling, meaning: doneMeaning, synonym: doneSynonym, antonym: doneAntonym };
  const onClickMap = { spelling: onStartTest, meaning: onStartMeaningTest, synonym: onStartSynonymTest, antonym: onStartAntonymTest };

  const streak = data.streaks?.[user.id] || 0;
  const myPoints = data.points?.[user.id] || 0;
  const myProgress = Object.entries(data.progress || {}).filter(([k]) => k.startsWith(user.id) && !k.includes("_meaning_"));
  const mastered = myProgress.filter(([, v]) => v.mastered).length;
  const spellingAcc = (() => {
    const ss = mySessions.filter(s => s.type !== "meaning" && s.type !== "synonym" && s.type !== "antonym" && typeof s.score === "number");
    return ss.length ? Math.round(ss.reduce((a, s) => a + s.score, 0) / ss.length) : 0;
  })();

  const level = Math.floor(myPoints / 100);
  const levelPct = myPoints % 100;
  const doneCount = [doneSpelling, doneMeaning, doneSynonym, doneAntonym].filter(Boolean).length;

  return (
    <div style={{ minHeight: "100vh", background: "#020617", color: "white", fontFamily: "'Exo 2', sans-serif", position: "relative", overflow: "hidden" }}>

      <style>{`
        @keyframes spaceFloat {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-10px); }
        }
        @keyframes spacePulseGlow {
          0%, 100% { box-shadow: 0 0 40px rgba(249,115,22,0.6); }
          50% { box-shadow: 0 0 70px rgba(249,115,22,1), 0 0 100px rgba(249,115,22,0.4); }
        }
      `}</style>

      {/* Star field */}
      <div style={{ position: "fixed", inset: 0, ...STARS_BG, pointerEvents: "none" }} />
      <div style={{ position: "fixed", inset: 0, ...NEBULA_BG, pointerEvents: "none" }} />

      <div style={{ position: "relative", zIndex: 1, maxWidth: 480, margin: "0 auto" }}>

        {/* Header */}
        <header style={{ ...GLASS, margin: 16, borderRadius: 16, padding: 16 }}>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <div style={{ position: "relative" }}>
                <div style={{ width: 56, height: 56, borderRadius: "50%", background: "linear-gradient(135deg, #a855f7, #ec4899)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: 24, fontFamily: "'Orbitron', sans-serif", fontWeight: 700 }}>
                  {user.avatar || user.name[0]}
                </div>
                <div style={{ position: "absolute", bottom: -2, right: -2, width: 20, height: 20, background: "#10b981", borderRadius: "50%", border: "2px solid #020617", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  <span style={{ fontSize: 10, fontWeight: 700, color: "#020617" }}>{streak}</span>
                </div>
              </div>
              <div>
                <p style={{ color: "#c084fc", fontSize: 11, fontWeight: 500, textTransform: "uppercase", letterSpacing: "0.1em" }}>Space Cadet</p>
                <h1 style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 18, fontWeight: 700 }}>{user.name.toUpperCase()}</h1>
              </div>
            </div>
            <div style={{ textAlign: "right" }}>
              <div style={{ display: "flex", alignItems: "center", gap: 4, color: "#fbbf24", justifyContent: "flex-end" }}>
                <span style={{ fontSize: 16 }}>⭐</span>
                <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 18, fontWeight: 700 }}>{myPoints.toLocaleString()}</span>
              </div>
              <p style={{ color: "#64748b", fontSize: 11 }}>Star Points</p>
            </div>
          </div>
          {/* Level progress */}
          <div style={{ marginTop: 12 }}>
            <div style={{ display: "flex", justifyContent: "space-between", fontSize: 11, color: "#64748b", marginBottom: 4 }}>
              <span>Level {level} ({level * 100} pts)</span>
              <span>{levelPct} / 100 pts to Level {level + 1}</span>
            </div>
            <div style={{ height: 8, background: "#1e293b", borderRadius: 999, overflow: "hidden" }}>
              <div style={{ height: "100%", background: "linear-gradient(to right, #a855f7, #ec4899, #f97316)", borderRadius: 999, width: `${levelPct}%`, transition: "width .5s" }} />
            </div>
          </div>
        </header>

        {/* Daily message */}
        <div style={{ padding: "0 16px" }}>
          <DailyMessage userName={user.name} streak={streak} bgGradient="linear-gradient(135deg, #a855f7 0%, #3b82f6 100%)" />
        </div>

        {/* Daily mission */}
        <div style={{ padding: "0 16px", marginBottom: 24 }}>
          <div style={{ ...GLASS, borderRadius: 12, padding: 16, borderLeft: "4px solid #22d3ee" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, color: "#22d3ee", marginBottom: 4 }}>
              <span style={{ fontSize: 13, fontWeight: 600, textTransform: "uppercase", letterSpacing: "0.05em" }}>⚡ Daily Mission</span>
            </div>
            <p style={{ fontWeight: 500, marginBottom: 12 }}>Complete all 4 quizzes to earn bonus star points!</p>
            <div style={{ display: "flex", gap: 8 }}>
              {[doneSpelling, doneMeaning, doneSynonym, doneAntonym].map((done, i) => (
                <div key={i} style={{ width: 32, height: 32, borderRadius: "50%", background: done ? "#10b981" : "#1e293b", border: done ? "none" : "2px dashed #475569", display: "flex", alignItems: "center", justifyContent: "center" }}>
                  {done
                    ? <svg style={{ width: 16, height: 16 }} fill="none" stroke="white" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"/></svg>
                    : <span style={{ fontSize: 11, color: "#64748b" }}>{i + 1}</span>
                  }
                </div>
              ))}
              <div style={{ marginLeft: 8, fontSize: 13, color: "#64748b", display: "flex", alignItems: "center" }}>{doneCount}/4 complete</div>
            </div>
          </div>
        </div>

        {/* Planet navigation */}
        <div style={{ padding: "0 16px", marginBottom: 24 }}>
          <h2 style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 11, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.2em", marginBottom: 24, textAlign: "center" }}>Choose Your Destination</h2>

          <div style={{ position: "relative", height: 360 }}>
            {/* Sun */}
            <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 72, height: 72, borderRadius: "50%", background: "linear-gradient(135deg, #fde68a, #f97316, #ef4444)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 0 40px rgba(249,115,22,0.6)", zIndex: 2, animation: "spacePulseGlow 2s ease-in-out infinite" }}>
              <span style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 9, fontWeight: 700, color: "white", opacity: 0.9 }}>HOME</span>
            </div>
            {/* Orbit rings — use min() so they never exceed the container width on narrow screens */}
            <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "min(200px, 68%)", height: "min(200px, 68%)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: "50%", pointerEvents: "none" }} />
            <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: "min(300px, 98%)", height: "min(300px, 98%)", border: "1px solid rgba(255,255,255,0.05)", borderRadius: "50%", pointerEvents: "none" }} />

            {/* Planets */}
            {PLANETS.map((planet, i) => {
              const done = doneMap[planet.key];
              const onClick = onClickMap[planet.key];
              const pos = PLANET_POSITIONS[i];
              const floatDelay = ["0s", "0.5s", "1s", "0s"][i];
              return (
                <div
                  key={planet.key}
                  onClick={onClick}
                  style={{ position: "absolute", ...pos, cursor: "pointer", display: "flex", flexDirection: "column", alignItems: "center", opacity: done ? 0.65 : 1, animation: `spaceFloat 3s ease-in-out ${floatDelay} infinite` }}
                >
                  <div style={{ width: planet.size, height: planet.size, borderRadius: "50%", background: planet.bg, boxShadow: planet.shadow, display: "flex", alignItems: "center", justifyContent: "center", position: "relative" }}>
                    <div style={{ position: "absolute", inset: 8, borderRadius: "50%", border: "1px solid rgba(255,255,255,0.2)" }} />
                    {done && (
                      <div style={{ position: "absolute", top: "50%", left: "50%", transform: "translate(-50%, -50%)", width: 40, height: 40, borderRadius: "50%", background: "rgba(34,197,94,0.9)", display: "flex", alignItems: "center", justifyContent: "center", boxShadow: "0 0 20px rgba(34,197,94,0.5)" }}>
                        <svg style={{ width: 20, height: 20 }} fill="none" stroke="white" viewBox="0 0 24 24"><path strokeLinecap="round" strokeLinejoin="round" strokeWidth={3} d="M5 13l4 4L19 7"/></svg>
                      </div>
                    )}
                  </div>
                  <p style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 10, marginTop: 6, color: done ? "rgba(255,255,255,0.4)" : "rgba(255,255,255,0.8)", textAlign: "center" }}>{planet.label}</p>
                  {done && <p style={{ fontSize: 11, color: "#10b981" }}>Done!</p>}
                </div>
              );
            })}
          </div>
        </div>

        {/* Stats panel */}
        <div style={{ padding: "0 16px 32px" }}>
          <div style={{ ...GLASS, borderRadius: 12, padding: 16 }}>
            <h3 style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 10, color: "#64748b", textTransform: "uppercase", letterSpacing: "0.1em", marginBottom: 12 }}>Mission Stats</h3>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 16, textAlign: "center" }}>
              {[
                { val: mastered, label: "Words Mastered", color: "white" },
                { val: `${spellingAcc}%`, label: "Accuracy", color: "#10b981" },
                { val: streak, label: "Day Streak", color: "#fbbf24" },
              ].map(({ val, label, color }, i) => (
                <div key={label} style={{ borderLeft: i > 0 ? "1px solid #1e293b" : "none" }}>
                  <p style={{ fontFamily: "'Orbitron', sans-serif", fontSize: 22, fontWeight: 700, color }}>{val}</p>
                  <p style={{ fontSize: 11, color: "#64748b" }}>{label}</p>
                </div>
              ))}
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
